import { useAppStateAssetGoalsListStore } from '../../../../common/store/AppStateStore.zustand';
import React, { useEffect, useMemo, useState } from 'react';
import { AssetReport } from '../../../assetReport/type/AssetReport.type';
import { numberWithCommas, percentageString } from '../../../../common/util/NumberUtils';
import { useAssetGoalsAPI } from '../../api/AssetGoals.api';
import { AssetGoals } from '../../type/AssetGoals.type';
import { calculateMonthlyRequiredIncrement } from '../../util/AssetGoalsUtils';

export interface AddAssetGoalsModalData {
  isOpen: boolean;
  onClose: () => void;
}

const AddAssetGoalsModal = ({ isOpen, onClose }: AddAssetGoalsModalData) => {
  const { insertAssetGoals, getAssetGoalsList } = useAssetGoalsAPI();
  const { setAssetGoalsList } = useAppStateAssetGoalsListStore();
  const [newAssetGoals, setNewAssetGoals] = useState<AssetGoals>({
    id: '',
    name: '',
    comment: '',
    startYearMonth: new Date().toISOString().slice(0, 7),
    startAmount: 0,
    endYearMonth: new Date().toISOString().slice(0, 7),
    endAmount: 0,
    annualReturnRate: 0,
  });

  useEffect(() => {
    setNewAssetGoals({
      id: '',
      name: '',
      comment: '',
      startYearMonth: new Date().toISOString().slice(0, 7),
      startAmount: 0,
      endYearMonth: new Date().toISOString().slice(0, 7),
      endAmount: 0,
      annualReturnRate: 0,
    });
  }, [isOpen]);

  const validInputYn = useMemo(
    () =>
      newAssetGoals.name &&
      newAssetGoals.endAmount &&
      newAssetGoals.startYearMonth &&
      newAssetGoals.endYearMonth &&
      newAssetGoals.startYearMonth < newAssetGoals.endYearMonth,
    [newAssetGoals],
  );

  const handleCloseClick = () => {
    onClose();
  };

  const handleAddClick = async () => {
    await insertAssetGoals(newAssetGoals);
    const { data } = await getAssetGoalsList();
    setAssetGoalsList(data);
    onClose();
  };

  const handleNewAssetGoalsChange = (target: string, value: any) => {
    setNewAssetGoals({
      ...newAssetGoals,
      [target]: value,
    });
  };

  return isOpen ? (
    <div className="fixed h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[600px] shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">목표 추가</h2>
              <p className="text-gray-500 text-sm mt-1">목표를 설정하고 달성을 위한 계획을 세워보세요</p>
            </div>
            <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div className="p-8">
          <div className="space-y-6">
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">이름</label>
              <input
                type="text"
                value={newAssetGoals.name || ''}
                onChange={(e) => handleNewAssetGoalsChange('name', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                placeholder="예: 주택 구매 자금"
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">시작년월</label>
                <input
                  type="month"
                  value={newAssetGoals.startYearMonth}
                  onChange={(e) => handleNewAssetGoalsChange('startYearMonth', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                />
              </div>
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">종료년월</label>
                <input
                  type="month"
                  value={newAssetGoals.endYearMonth}
                  onChange={(e) => handleNewAssetGoalsChange('endYearMonth', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">시작금액</label>
                <div className="relative">
                  <input
                    type="number"
                    value={newAssetGoals.startAmount ? newAssetGoals.startAmount : ''}
                    onChange={(e) => handleNewAssetGoalsChange('startAmount', Number(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors pr-12"
                    placeholder="0"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">원</span>
                </div>
              </div>
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">목표금액</label>
                <div className="relative">
                  <input
                    type="number"
                    value={newAssetGoals.endAmount ? newAssetGoals.endAmount : ''}
                    onChange={(e) => handleNewAssetGoalsChange('endAmount', Number(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors pr-12"
                    placeholder="0"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">원</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                기대 연평균 수익률
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newAssetGoals.annualReturnRate}
                  onChange={(e) => handleNewAssetGoalsChange('annualReturnRate', Number(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors pr-12"
                  placeholder="5"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
          </div>
          {validInputYn ? (
            <div className="mt-6 bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="mr-2">
                  <p className="text-sm text-indigo-700 font-medium">월 필요 적립액</p>
                  <p className="text-xs text-indigo-600 mt-1 break-keep">
                    목표 달성을 위해 매월 적립해야 할 금액입니다
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-700 break-keep">
                    {numberWithCommas(
                      calculateMonthlyRequiredIncrement(newAssetGoals) > newAssetGoals.endAmount
                        ? newAssetGoals.endAmount
                        : calculateMonthlyRequiredIncrement(newAssetGoals),
                    )}
                    원
                  </p>
                  <p className="text-xs text-indigo-600 mt-1">{newAssetGoals.annualReturnRate}% 수익률 기준</p>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className="p-6 pr-0">
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleCloseClick()}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 !rounded-button whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleAddClick}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md text-sm !rounded-button whitespace-nowrap"
              >
                목표 추가
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default AddAssetGoalsModal;
